import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { MessageCircle, X, Send, Loader2, Bot } from "lucide-react";

const SID_KEY = "dosacloud_chat_sid";

function getSessionId() {
  let sid = localStorage.getItem(SID_KEY);
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) || `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SID_KEY, sid);
  }
  return sid;
}

export const ChatWidget = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sidRef = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      api.get(`/chat/${sidRef.current}/history`)
        .then(({ data }) => { if (data.length) setMessages(data); })
        .catch(() => {});
    }
  }, [open, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat", { session_id: sidRef.current, message: text });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: t("auth.assistantError") }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle-btn"
        aria-label={t("auth.assistantTitle")}
        className="fixed bottom-6 right-6 z-[1000] flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
        style={{ backgroundColor: "#2563eb" }}
      >
        {open ? <X className="h-6 w-6" strokeWidth={2} /> : <MessageCircle className="h-6 w-6" strokeWidth={2} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-6 z-[1000] flex h-[520px] w-[92vw] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        >
          <div className="flex items-center gap-3 px-4 py-3 text-white" style={{ backgroundColor: "#0B2A4A" }}>
            <div className="flex h-9 w-9 items-center justify-center rounded-full" style={{ backgroundColor: "#2563eb" }}>
              <Bot className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">{t("auth.assistantTitle")}</div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-blue-200">DOSATRONIC</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-4 py-4" data-testid="chat-messages">
            <div className="flex gap-2">
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
                {t("auth.assistantGreeting")}
              </div>
            </div>
            {messages.map((m, i) => (
              <div key={`${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm shadow-sm ${
                    m.role === "user"
                      ? "rounded-br-sm bg-blue-600 text-white"
                      : "rounded-tl-sm bg-white text-slate-700"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl rounded-tl-sm bg-white px-3 py-2 text-sm text-slate-400 shadow-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> …
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-slate-200 bg-white p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={t("auth.assistantPlaceholder")}
              data-testid="chat-input"
              className="flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm outline-none focus:border-blue-400"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              data-testid="chat-send-btn"
              className="flex h-9 w-9 items-center justify-center rounded-full text-white transition disabled:opacity-40"
              style={{ backgroundColor: "#2563eb" }}
            >
              <Send className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
